import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CircularProgress, 
  Alert, 
  Grid, 
  Button, 
  List, 
  ListItem, 
  ListItemText, 
  Divider, 
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import { useDecodedToken } from '../utils/useDecodedToken';
import { api } from '../api/client';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptIcon from '@mui/icons-material/Receipt';

interface Charge {
  id: number;
  chargeId: number;
  name: string;
  chargeTimeType: {
    id: number;
    code: string;
    value: string;
  };
  dueDate: number[];
  chargeCalculationType: {
    id: number;
    code: string;
    value: string;
  };
  currency: {
    code: string;
    name: string;
    decimalPlaces: number;
    nameCode: string;
    displayLabel: string;
  };
  amount: number;
  amountPaid: number;
  amountWaived: number;
  amountWrittenOff: number;
  amountOutstanding: number;
  amountOrPercentage: number;
  penalty: boolean;
  chargePaymentMode: {
    id: number;
    code: string;
    value: string;
  };
  paid: boolean;
  waived: boolean;
  chargePayable: boolean;
}

interface Transaction {
  id: string;
  transactionId: string;
  amount: number;
  type: string;
  description: string;
  transactionDate: string;
  status: string;
  currency: string;
}

const ClientCharges: React.FC = () => {
  const user = useDecodedToken();
  const [charges, setCharges] = useState<Charge[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (user?.accountNumber) {
      fetchData();
    }
  }, [user?.accountNumber]);

  const fetchData = async () => {
    if (!user?.accountNumber) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Fetch charges using the new client controller endpoint
      const chargesResponse = await api.get(`/api/clients/${user.accountNumber}/charges`);
      const charges = chargesResponse.data.charges || chargesResponse.data || [];
      // Ensure it's always an array
      setCharges(Array.isArray(charges) ? charges : []);
      
      // Fetch client accounts to get loan IDs
      const accountsResponse = await api.get(`/api/clients/${user.accountNumber}/accounts`);
      const loanAccounts = accountsResponse.data.loanAccounts || [];
      
      // Fetch transactions for each loan using the loan associations endpoint
      const allTransactions: any[] = [];
      for (const loan of loanAccounts) {
        try {
          const loanTransactionsResponse = await api.get(`/api/loans/${loan.id}/associations?associations=transactions`);
          const loanTransactions = loanTransactionsResponse.data.transactions || [];
          if (Array.isArray(loanTransactions)) {
            allTransactions.push(...loanTransactions);
          }
        } catch (loanErr: any) {
          console.error(`Failed to fetch transactions for loan ${loan.id}:`, loanErr);
        }
      }
      
      setTransactions(allTransactions);
    } catch (err: any) {
      console.error('Failed to fetch client data:', err);
      setError('Failed to load client charges and transactions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleTabChange = (_: any, newValue: number) => {
    setTab(newValue);
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDueDate = (dueDateArray: number[]) => {
    if (!dueDateArray || dueDateArray.length !== 3) return 'N/A';
    const [year, month, day] = dueDateArray;
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'outstanding':
        return 'error';
      case 'waived':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getTransactionColor = (type: string) => {
    return type.toUpperCase() === 'DEBIT' ? 'error.main' : 'success.main';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Client Charges & Transactions
        </Typography>
        <Button 
          variant="outlined" 
          onClick={handleRefresh} 
          disabled={loading}
          startIcon={<RefreshIcon />}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab 
              icon={<AccountBalanceIcon />} 
              label="Charges" 
              iconPosition="start"
            />
            <Tab 
              icon={<ReceiptIcon />} 
              label="Transactions" 
              iconPosition="start"
            />
          </Tabs>

          {tab === 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                <AccountBalanceIcon sx={{ mb: '-4px', mr: 1, color: 'primary.main' }} />
                Charges
              </Typography>
              
              {charges.length === 0 ? (
                <Typography color="text.secondary">No charges found.</Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ background: '#f0f4f8' }}>
                        <TableCell><b>Name</b></TableCell>
                        <TableCell><b>Fee/Penalty</b></TableCell>
                        <TableCell><b>Payment due at</b></TableCell>
                        <TableCell><b>Due as of</b></TableCell>
                        <TableCell><b>Calculation Type</b></TableCell>
                        <TableCell><b>Due</b></TableCell>
                        <TableCell><b>Paid</b></TableCell>
                        <TableCell><b>Waived</b></TableCell>
                        <TableCell><b>Outstanding</b></TableCell>
                        <TableCell><b>Actions</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {charges.map((charge) => (
                        <TableRow key={charge.id}>
                          <TableCell>{charge.name || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={charge.penalty ? 'Penalty' : 'Fee'} 
                              color={charge.penalty ? 'error' : 'primary'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{charge.chargeTimeType?.value || 'N/A'}</TableCell>
                          <TableCell>{formatDueDate(charge.dueDate)}</TableCell>
                          <TableCell>{charge.chargeCalculationType?.value || 'N/A'}</TableCell>
                          <TableCell>{charge.amount?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>{charge.amountPaid?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>{charge.amountWaived?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>{charge.amountOutstanding?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>
                            <Button 
                              variant="outlined" 
                              size="small"
                              disabled={charge.paid}
                            >
                              {charge.paid ? 'Paid' : 'Pay'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {tab === 1 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                <ReceiptIcon sx={{ mb: '-4px', mr: 1, color: 'primary.main' }} />
                Transactions
              </Typography>
              
              {transactions.length === 0 ? (
                <Typography color="text.secondary">No transactions found.</Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ background: '#f0f4f8' }}>
                        <TableCell><b>Date</b></TableCell>
                        <TableCell><b>Transaction ID</b></TableCell>
                        <TableCell><b>Type</b></TableCell>
                        <TableCell><b>Amount</b></TableCell>
                        <TableCell><b>Description</b></TableCell>
                        <TableCell><b>Status</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                                              {transactions.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell>{formatDate(transaction.transactionDate)}</TableCell>
                          <TableCell>{transaction.transactionId || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={transaction.type || 'N/A'} 
                              color={transaction.type?.toUpperCase() === 'DEBIT' ? 'error' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ color: getTransactionColor(transaction.type) }}>
                            {formatCurrency(transaction.amount || 0, transaction.currency || 'USD')}
                          </TableCell>
                          <TableCell>{transaction.description || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={transaction.status || 'N/A'} 
                              color={transaction.status?.toLowerCase() === 'success' ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClientCharges; 